import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import QuickVerifyModal from "@/components/QuickVerifyModal";
import { ShieldCheck, LogIn, IdCard, Clock, AlertTriangle } from "lucide-react";

/**
 * Lockout screen rendered when /api/streams/{id} or the WS connection
 * rejects this viewer with a structured 401/403. Two flavors:
 *   - auth_required          → sign-in CTA
 *   - verification_required  → /verify CTA, with the user's current
 *                              verification_status surfaced so they
 *                              know whether to upload (unverified)
 *                              or wait (pending) or contact us
 *                              (rejected).
 */
function statusCopy(status) {
  switch (status) {
    case "pending":
      return {
        icon: Clock,
        accent: "bg-amber-500",
        headline: "Your verification is under review",
        body: "We usually approve in under 24 hours. You'll get an email + push the moment we say yes — then live shows are wide open to you.",
      };
    case "rejected":
      return {
        icon: AlertTriangle,
        accent: "bg-rose-500",
        headline: "Your verification didn't go through",
        body: "Re-upload a clear photo of your US government-issued ID and a matching selfie. Most rejections are blurry photos or expired IDs.",
      };
    case "unverified":
    default:
      return {
        icon: IdCard,
        accent: "bg-[#1D78AB]",
        headline: "Verify your account to enter live shows",
        body: "Bidorah live shows are an approved-accounts-only zone — it's how we keep the marketplace clean for sellers and buyers. Upload your US photo ID + a quick selfie. Takes 90 seconds.",
      };
  }
}

export default function LiveStreamAccessGate({ gate }) {
  const isAuth = gate?.code === "auth_required" || gate?.status === 401;
  const copy = isAuth
    ? {
        icon: LogIn,
        accent: "bg-zinc-900",
        headline: "Sign in to enter the live show",
        body: "Live shows are for approved accounts only. Sign in to your verified Bidorah account, or create one — it takes a couple minutes.",
      }
    : statusCopy(gate?.verification_status || "unverified");

  const Icon = copy.icon;
  const primaryCta = isAuth ? "/login" : gate?.action_url || "/verify";
  const isRejected = gate?.verification_status === "rejected";
  const isPending = gate?.verification_status === "pending";
  // Pending users have already submitted — no in-modal flow yet, just
  // direct them back to the verify page where they can re-submit if
  // needed. Everyone else (unverified / rejected) gets the modal.
  const useModal = !isAuth && !isPending;
  const primaryLabel = isAuth
    ? "Sign in"
    : isRejected
    ? "Re-submit verification"
    : isPending
    ? "View verification"
    : "Verify now";
  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onPrimary = (e) => {
    if (useModal) {
      e.preventDefault();
      setModalOpen(true);
    }
  };

  // When the user submits in-modal we flip into a celebratory state so
  // they see clear progress without leaving the show page.
  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-50" data-testid="live-stream-access-gate-submitted">
        <Navbar />
        <div className="max-w-xl mx-auto px-5 pt-12">
          <div className="rounded-3xl border border-emerald-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-500 text-white grid place-items-center mb-5 shadow-md">
              <ShieldCheck size={30} strokeWidth={2.2} />
            </div>
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700">
              Submitted for review
            </p>
            <h1 className="font-display font-black text-3xl tracking-tight text-zinc-900 mt-1.5 mb-3">
              We've got your ID
            </h1>
            <p className="text-sm text-zinc-600 leading-relaxed mb-6">
              Most approvals land in under 24 hours. The moment we say yes, this exact stream (and every other live show) will be wide open to you.
            </p>
            <Link
              to="/live"
              className="inline-block w-full rounded-full bg-[#1D78AB] hover:bg-[#176089] text-white font-black px-6 py-3 text-sm tracking-tight"
              data-testid="live-stream-access-gate-browse"
            >
              Browse live shows while you wait
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="live-stream-access-gate">
      <Navbar />
      <div className="max-w-xl mx-auto px-5 pt-12">
        <div className="rounded-3xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
          <div className={`mx-auto h-16 w-16 rounded-2xl ${copy.accent} text-white grid place-items-center mb-5 shadow-md`}>
            <Icon size={30} strokeWidth={2.2} />
          </div>
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-500">
            Approved accounts only
          </p>
          <h1 className="font-display font-black text-3xl tracking-tight text-zinc-900 mt-1.5 mb-3">
            {copy.headline}
          </h1>
          <p className="text-sm text-zinc-600 leading-relaxed mb-6">{copy.body}</p>

          <Link
            to={primaryCta}
            onClick={onPrimary}
            className="inline-block w-full rounded-full bg-[#1D78AB] hover:bg-[#176089] text-white font-black px-6 py-3 text-sm tracking-tight"
            data-testid="live-stream-access-gate-cta"
          >
            {primaryLabel}
          </Link>

          {!isAuth && (
            <Link
              to="/live"
              className="block mt-3 text-sm text-zinc-500 hover:text-zinc-800"
              data-testid="live-stream-access-gate-back"
            >
              ← Back to live shows
            </Link>
          )}

          {isAuth && (
            <Link
              to="/register"
              className="block mt-3 text-sm text-zinc-500 hover:text-zinc-800"
              data-testid="live-stream-access-gate-register"
            >
              New here? Create an account →
            </Link>
          )}

          <div className="mt-6 pt-5 border-t border-zinc-100 flex items-center justify-center gap-2 text-[11px] text-zinc-400">
            <ShieldCheck size={12} /> Trusted-network of approved sellers and buyers
          </div>
        </div>
      </div>

      <QuickVerifyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmitted={() => {
          setModalOpen(false);
          setSubmitted(true);
        }}
      />
    </div>
  );
}
